import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import Entity from "_framework/entities/entity";
import Guid from "_shared/guids/guid";
import BookmarkedTeam from "marketplace/bookmarks/team-bookmarks/bookmarked-team";
import BookmarkedTeamListItemSchema, {
  BookmarkedTeamListItemJSON,
  BookmarkedTeamListItemParams,
} from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-list-item-schema";
import Team from "marketplace/teams/team";

/**
 * One row of the caller's team bookmarks: the bookmark, and the bookmarked team when the
 * caller may read it. A row without the team is still a bookmark that the caller can delete.
 */
export default class BookmarkedTeamListItem
  extends Entity<BookmarkedTeamListItem, BookmarkedTeamListItemJSON, BookmarkedTeamListItemParams> {
  public static create(values: BookmarkedTeamListItemParams): BookmarkedTeamListItem {
    const parsed = BookmarkedTeamListItemSchema.validate(values);
    return new BookmarkedTeamListItem(parsed);
  }

  public static fromJSON(json: BookmarkedTeamListItemJSON): BookmarkedTeamListItem {
    const parsed = BookmarkedTeamListItemSchema.validateJSON(json);
    return new BookmarkedTeamListItem({
      bookmark: BookmarkedTeam.fromJSON({
        id: parsed.id,
        createdDate: parsed.createdDate,
        teamId: parsed.teamId,
      }),
      team: parsed.team ? Team.fromJSON({ ...parsed.team, name: parsed.team.name ?? "" }) : undefined,
      unavailableReason: parsed.unavailableReason ?? undefined,
    });
  }

  public static fromJSONArray(jsonArray: BookmarkedTeamListItemJSON[]): BookmarkedTeamListItem[] {
    return jsonArray.map((json) => BookmarkedTeamListItem.fromJSON(json));
  }

  public readonly bookmark: BookmarkedTeam;
  public readonly team?: Team;
  public readonly unavailableReason?: Extract<UnavailableReason, "unauthorized">;

  /** The bookmark's id, so a page of rows can be keyed like any other page. */
  public get id(): Guid {
    return this.bookmark.id;
  }

  public get teamId(): Guid {
    return this.bookmark.teamId;
  }

  /** True when the caller may not read the bookmarked team. */
  public get isUnavailable(): boolean {
    return this.unavailableReason !== undefined;
  }

  public constructor(params: BookmarkedTeamListItemParams) {
    super(params);
    this.bookmark = params.bookmark;
    this.team = params.team;
    this.unavailableReason = params.unavailableReason;
  }

  public toJSON(): BookmarkedTeamListItemJSON {
    return {
      ...this.bookmark.toJSON(),
      team: this.team?.toJSON() ?? null,
      unavailableReason: this.unavailableReason ?? null,
    };
  }

  public valueOf(): BookmarkedTeamListItemJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Bookmarked Team List Item: ${this.bookmark.id.toString()} (${this.unavailableReason ?? "visible"})`;
  }

  public isEqualTo(other: BookmarkedTeamListItem | null | undefined): boolean {
    if (!other) return false;
    const sameTeam = this.team && other.team
      ? this.team.isEqualTo(other.team)
      : !this.team && !other.team;
    return this.bookmark.isEqualTo(other.bookmark) && this.unavailableReason === other.unavailableReason && sameTeam;
  }
}
